import { white } from '@synoptic/ui-kit/palette.js';
import { responsiveStyle } from '@synoptic/ui-kit/responsive/responsive-style.js';
import { themeVars } from '@synoptic/ui-kit/theme/vars.js';
import {
  borderRadius,
  boxShadows,
  fontWeight,
  space,
} from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { ellipsis } from '@synoptic/ui-kit/utility.css.js';
import { globalStyle, style, styleVariants } from '@vanilla-extract/css';

export const homeStreamCard = style({
  display: 'flex',
  flexDirection: 'column',
  padding: space.s16,
  height: '100%',
  borderRadius: borderRadius.r12,
  color: themeVars.color.text.primary,
  background:
    'linear-gradient(85.59deg, #FBFBFD -17.34%, #ECE9F8 38.2%, #D3E1FF 102.73%)',
});

export const homeStreamCardMini = style({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: borderRadius.r12,
  backgroundColor: themeVars.container.primary.background.idle,
  padding: space.s12,
  height: '100%',
  border: `1px solid ${themeVars.container.border.default}`,

  transition: 'box-shadow 0.2s',

  ':hover': {
    boxShadow: boxShadows.default,
  },
});

export const homeStreamCardMiniLink = style({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

export const homeStreamCardTitle = style({
  ...typography.titleMd,
  fontWeight: fontWeight.bold,
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
});

export const streamPackageCardHeader = style({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: space.s16,
});

export const homeStreamCardTitleVariants = styleVariants({
  medium: {
    ...typography.titleMd,
  },
  small: {
    ...typography.titleSm,
  },
});

export const homeStreamCardDescription = style({
  ...typography.bodyMd,
  marginTop: space.s4,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
  color: themeVars.container.foreground.secondary,
});

export const homeStreamCardHeader = style({
  display: 'flex',
  flexDirection: 'row',
  gap: space.s4,
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: space.s12,
  color: themeVars.color.text.primary,
  flexWrap: 'wrap',
});

globalStyle(`.${homeStreamCard} .${homeStreamCardDescription}`, {
  color: themeVars.color.text.tertiary,
});

export const homeStreamCardMiniHeader = style({
  display: 'flex',
  flexDirection: 'row',
  gap: space.s4,
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  marginBottom: space.s8,
});

export const homeStreamCardAuthor = style({
  display: 'flex',
  flexDirection: 'row',
  gap: space.s8,
  overflow: 'hidden',
  alignItems: 'center',
});

export const homeStreamCardAuthorUsername = style([
  typography['bodySm'],
  {
    color: themeVars.container.foreground.tertiary,
  },
  ellipsis,
]);

export const homeStreamCardAdditionalInfo = style([
  {
    display: 'flex',
    flexDirection: 'row',
    gap: space.s16,
    rowGap: space.s2,
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: `${space.s16}px 0px`,
  },
  responsiveStyle({
    mobile: {
      flexWrap: 'wrap',
    },
    tablet: {
      flexWrap: 'nowrap',
    },
  }),
]);

export const homeStreamCardMiniAdditionalInfo = style([
  homeStreamCardAdditionalInfo,
  {
    margin: 0,
    paddingTop: space.s12,
    marginTop: 'auto',
    flexWrap: 'nowrap',
  },
]);

export const homeStreamCardAdditionalInfoRight = style({
  display: 'flex',
  flexDirection: 'row',
  gap: space.s12,
  alignItems: 'center',
  marginLeft: 'auto',
});

export const homeCardBadge = style({
  display: 'flex',
  selectors: {
    '&:is(&)': {
      backgroundColor: themeVars.container.primary.background.idle,
      border: `1px solid ${themeVars.container.border.secondary}`,
      padding: `${space.s4}px ${space.s8}px`,
      ...typography.labelSm,
    },
  },
});

export const homeStreamCardPriceValues = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'center',
});

export const homeStreamCardPriceTag = style({
  display: 'flex',
  alignItems: 'flex-start',
  maxHeight: 'fit-content',
  justifyContent: 'center',
  ...typography.labelSm,
});

globalStyle(`.${homeStreamCardPriceTag} :where(svg)`, {
  color: themeVars.container.foreground.tertiary,
});

export const homeViewDetailsButton = style({
  marginTop: 'auto',
});

export const homeStreamCardFeaturedBadge = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: space.s2,
  ...typography.labelMd,
  color: themeVars.container.foreground.tertiary,
});

export const homeStreamCardFollowersBlock = style([
  homeStreamCardFeaturedBadge,
]);

export const streamPackageCard = style([
  {
    display: 'flex',
    padding: space.s24,
    height: '100%',
    borderRadius: borderRadius.r12,
    border: `1px solid ${themeVars.container.border.default}`,
    background: `linear-gradient(284.24deg, #FFBFA3 -13.43%, light-dark(#9D88B8, #765D97) 38.46%, light-dark(#7874C0, #4B4798) 77.55%)`,
  },
  responsiveStyle({
    mobile: {
      flexDirection: 'column',
      gap: 0,
    },
    tablet: {
      flexDirection: 'row',
      gap: space.s32,
    },
  }),
]);

export const streamPackageCardTitle = style({
  ...typography.titleLg,
  color: themeVars.color.text.inverse,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: 2,
});

export const streamPackageCardLeft = style([
  {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 'min(300px, 100%)',
  },
  responsiveStyle({
    mobile: {
      maxWidth: '100%',
    },
    tablet: {
      maxWidth: '350px',
    },
  }),
]);

globalStyle(`.${streamPackageCardLeft} .${homeStreamCardPriceValues}`, {
  ...typography.labelMd,
  color: themeVars.color.text.inverse,
});

export const streamPackageCardRight = style({
  display: 'grid',
  columnGap: space.s12,
  rowGap: space.s8,
  gridAutoRows: 'max-content',
  ...responsiveStyle({
    mobile: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
    tablet: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  }),
});

export const streamPackageCardStreamItem = style([
  {
    borderRadius: borderRadius.r12,
    padding: space.s16,
    display: 'flex',
    flexDirection: 'column',
    gap: space.s4,
    backgroundColor: themeVars.container.primary.background.idle,
    border: `1px solid ${themeVars.container.border.default}`,
  },
]);

export const streamPackageCardBenefits = style({
  ...typography.bodyMd,
  color: themeVars.container.foreground.secondary,
  listStyle: 'none',
  margin: 0,
  marginTop: space.s16,
  display: 'flex',
  flexDirection: 'column',
  gap: space.s4,
  borderRadius: borderRadius.r8,
  padding: space.s12,
  backgroundColor: white[50],
});

export const streamPackageCardBenefit = style({
  display: 'flex',
  flexDirection: 'row',
  gap: space.s4,
  alignItems: 'center',
  color: themeVars.color.text.inverse,
});

globalStyle(`.${streamPackageCardBenefit} :where(svg)`, {
  flexShrink: 0,
});

export const streamPackageCardFooter = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  marginTop: 'auto',
});

export const homeStreamPackageAdditionalInfo = style([
  homeStreamCardAdditionalInfo,
  {
    ...typography.bodySm,
    color: themeVars.color.bg.inverse.active,
    margin: 0,
    justifyContent: 'flex-end',
  },
]);

export const homeStreamPackageAuthorInfo = style([
  homeStreamCardAdditionalInfo,

  {
    borderBottom: `1px solid ${themeVars.container.border.secondary}`,
    marginBottom: 0,
    paddingBottom: space.s16,
    flexWrap: 'nowrap',
  },
  responsiveStyle({
    mobile: {
      marginBottom: space.s16,
    },
  }),
]);

globalStyle(
  `.${homeStreamPackageAuthorInfo} .${homeStreamCardAuthorUsername},
    .${homeStreamPackageAuthorInfo} .${homeStreamCardFollowersBlock}`,
  {
    color: themeVars.color.text.inverse,
  },
);

globalStyle(
  `.${streamPackageCardLeft} .${homeStreamCardPriceTag} :where(svg)`,
  {
    color: themeVars.color.bg.inverse.active,
  },
);

export const streamTypeLabel = style({
  selectors: {
    '&:is(&)': {
      backgroundColor: 'transparent',
      color: themeVars.container.foreground.tertiary,
      border: 'none',
      padding: 0,
    },
    '&:is(&:hover)': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
});

export const streamTypeLabelSelected = style({
  selectors: {
    '&:is(&)': {
      textDecoration: 'underline',
      color: themeVars.container.foreground.primary,
    },
  },
});

export const streamCardFooter = style({
  marginTop: 'auto',
});

globalStyle(`.${homeStreamCardHeader} .${homeStreamCardPriceTag} :where(svg)`, {
  color: themeVars.color.icon.secondary,
});

globalStyle(
  `.${homeStreamCard} .${homeStreamCardAuthorUsername},
  .${homeStreamCard} .${homeStreamCardFeaturedBadge}`,
  {
    color: themeVars.color.text.tertiary,
  },
);

export const homeStreamCardViewDetailsButton = style([
  homeViewDetailsButton,
  {
    selectors: {
      '&:is(&)': {
        color: themeVars.color.text.brand,
        boxShadow: `inset 0 0 0 1px ${themeVars.color.bg.brand.active}`,
      },
    },
  },
]);
